import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    apiListCustomers,
    apiCreateCustomer,
    apiUpdateCustomer,
    apiDeleteCustomer,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Building2, Pencil, Power, AlertCircle, Trash2 } from "lucide-react";

export default function AdminCustomers() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setCustomers(await apiListCustomers());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function toggleActive(c) {
        try {
            await apiUpdateCustomer(c.customer_id, { active: !c.active });
            toast.success(c.active ? "Customer deactivated" : "Customer activated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function confirmDelete() {
        if (!deleting) return;
        try {
            await apiDeleteCustomer(deleting.customer_id);
            toast.success("Customer deleted");
            setDeleting(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <Layout active="customers">
            <PageHeader
                title="Customers"
                subtitle="Organisations that own one or more projects."
                actions={
                    <Button
                        onClick={() => setAddOpen(true)}
                        data-testid="add-customer-btn"
                        className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        <Building2 className="h-4 w-4 mr-1.5" />
                        Add customer
                    </Button>
                }
            />

            <div
                className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                data-testid="customers-table"
            >
                <div className="grid grid-cols-[minmax(200px,2fr)_100px_minmax(180px,1.5fr)_140px_100px_200px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                    <div>Name</div>
                    <div>Code</div>
                    <div>Contact</div>
                    <div>Phone</div>
                    <div>Status</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">Loading customers…</div>
                ) : customers.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="text-sm text-ntt-muted mb-3">No customers yet.</div>
                        <Button
                            onClick={() => setAddOpen(true)}
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            Add your first customer
                        </Button>
                    </div>
                ) : (
                    customers.map((c) => (
                        <div
                            key={c.customer_id}
                            data-testid={`customer-row-${c.customer_id}`}
                            className="grid grid-cols-[minmax(200px,2fr)_100px_minmax(180px,1.5fr)_140px_100px_200px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                        >
                            <div className="text-[13px] font-medium text-[#070F26] truncate">{c.name}</div>
                            <div className="text-[12px] font-mono text-ntt-muted">{c.code || "—"}</div>
                            <div className="text-[12px] text-[#2B2F3A] truncate">
                                {c.contact_person ? (
                                    <>
                                        {c.contact_person}
                                        {c.contact_email ? (
                                            <div className="text-[11px] text-ntt-muted truncate">
                                                {c.contact_email}
                                            </div>
                                        ) : null}
                                    </>
                                ) : c.contact_email ? (
                                    <span className="text-ntt-muted">{c.contact_email}</span>
                                ) : (
                                    <span className="text-ntt-muted italic">—</span>
                                )}
                            </div>
                            <div className="text-[12px] text-ntt-muted">{c.contact_phone || "—"}</div>
                            <div>
                                <StatusPill
                                    label={c.active ? "Active" : "Inactive"}
                                    tone={c.active ? "green" : "gray"}
                                />
                            </div>
                            <div className="flex items-center justify-end gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditing(c)}
                                    data-testid={`edit-customer-${c.customer_id}`}
                                    className="h-8 text-[12px]"
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => toggleActive(c)}
                                    data-testid={`toggle-customer-${c.customer_id}`}
                                    className={`h-8 text-[12px] ${c.active ? "text-red-600 hover:text-red-700" : "text-emerald-700 hover:text-emerald-800"}`}
                                >
                                    <Power className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDeleting(c)}
                                    data-testid={`delete-customer-${c.customer_id}`}
                                    className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <CustomerFormDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                mode="create"
                onDone={load}
            />
            <CustomerFormDialog
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                mode="edit"
                customer={editing}
                onDone={() => {
                    setEditing(null);
                    load();
                }}
            />

            <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
                <AlertDialogContent data-testid="delete-customer-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete customer?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently delete <b>{deleting?.name}</b>. If any project still belongs
                            to this customer, deletion will be blocked — reassign or deactivate first.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            data-testid="delete-customer-confirm-btn"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}

function CustomerFormDialog({ open, onOpenChange, mode, customer, onDone }) {
    const [form, setForm] = useState({
        name: "",
        code: "",
        contact_person: "",
        contact_email: "",
        contact_phone: "",
        address: "",
        notes: "",
    });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                name: customer?.name || "",
                code: customer?.code || "",
                contact_person: customer?.contact_person || "",
                contact_email: customer?.contact_email || "",
                contact_phone: customer?.contact_phone || "",
                address: customer?.address || "",
                notes: customer?.notes || "",
            });
            setError("");
        }
    }, [open, customer]);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                code: form.code.trim() || null,
                contact_person: form.contact_person.trim() || null,
                contact_email: form.contact_email.trim() || null,
                contact_phone: form.contact_phone.trim() || null,
                address: form.address.trim() || null,
                notes: form.notes.trim() || null,
            };
            if (mode === "create") {
                await apiCreateCustomer(payload);
                toast.success("Customer created");
            } else {
                await apiUpdateCustomer(customer.customer_id, payload);
                toast.success("Customer updated");
            }
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto"
                data-testid="customer-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Add customer" : "Edit customer"}</DialogTitle>
                    <DialogDescription>
                        The customer owns one or more projects — you'll link projects to this
                        customer next.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="customer-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Name</Label>
                            <Input
                                required
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                placeholder="Acme Corporation"
                                data-testid="customer-name-input"
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Code (optional)
                            </Label>
                            <Input
                                value={form.code}
                                onChange={(e) => set("code", e.target.value.toUpperCase())}
                                placeholder="ACME"
                                maxLength={16}
                                data-testid="customer-code-input"
                                className="h-10 font-mono uppercase"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Contact person
                            </Label>
                            <Input
                                value={form.contact_person}
                                onChange={(e) => set("contact_person", e.target.value)}
                                placeholder="Jane Doe"
                                data-testid="customer-contact-person-input"
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Email</Label>
                            <Input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => set("contact_email", e.target.value)}
                                placeholder="ops@example.com"
                                data-testid="customer-contact-email-input"
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Phone</Label>
                            <Input
                                value={form.contact_phone}
                                onChange={(e) => set("contact_phone", e.target.value)}
                                placeholder="+91 98765 43210"
                                data-testid="customer-contact-phone-input"
                                className="h-10"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Address
                            </Label>
                            <Input
                                value={form.address}
                                onChange={(e) => set("address", e.target.value)}
                                placeholder="City, Country"
                                data-testid="customer-address-input"
                                className="h-10"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Notes</Label>
                            <textarea
                                value={form.notes}
                                onChange={(e) => set("notes", e.target.value)}
                                placeholder="Anything the team should know…"
                                data-testid="customer-notes-input"
                                className="w-full min-h-[70px] rounded-md border border-ntt-border p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                            />
                        </div>
                    </div>
                    {error && (
                        <div
                            data-testid="customer-form-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="customer-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="customer-save-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
